/**
 * ManageLabelsDialog — admin/super-user UI to create, rename, recolor, set
 * billing rate config, and delete the company's job label catalog. Opened
 * from a "Labels" button on the Jobs page header.
 *
 * Each label can optionally carry a billing rate:
 *   rate_type: "none" | "fixed" ($/hr) | "multiplier" (× of job service_rate)
 * The invoice generators (Native PDF + QuickBooks) automatically apply the
 * resolved rate to any job that has the label. When a job has multiple
 * rate-bearing labels, the SINGLE highest resolved rate wins (no stacking).
 *
 * Backend:
 *   GET    /api/labels
 *   POST   /api/labels         { name, color, rate_type?, rate_value? }
 *   PUT    /api/labels/{id}    { name?, color?, rate_type?, rate_value? }
 *   DELETE /api/labels/{id}    — cascades to remove from every job
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Plus, Pencil, Trash2, Check, X, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LabelChip from './LabelChip';
import { toast } from 'sonner';

const API = window.__BACKEND_URL__;

const COLOR_PRESETS = [
  '#EF4444', '#F97316', '#F59E0B', '#FACC15',
  '#84CC16', '#22C55E', '#00C896', '#06B6D4',
  '#1E88E5', '#6366F1', '#A78BFA', '#EC4899',
];

// Render the inline rate summary ("$150/hr", "1.5×", "—") next to a label.
function RateSummary({ rateType, rateValue }) {
  if (rateType === 'fixed' && rateValue > 0) {
    return (
      <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,200,150,0.12)', color: '#00C896' }}>
        ${Number(rateValue).toFixed(2)}/hr
      </span>
    );
  }
  if (rateType === 'multiplier' && rateValue > 0) {
    return (
      <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(30,136,229,0.12)', color: '#1E88E5' }}>
        {Number(rateValue)}× base
      </span>
    );
  }
  return <span className="text-xs italic" style={{ color: '#475569' }}>no rate</span>;
}

// Inline rate-config control used in both Create and Edit forms.
function RateConfig({ rateType, rateValue, onChange, idPrefix }) {
  const setType = (next) => {
    onChange({ rate_type: next, rate_value: next === 'none' ? null : (rateValue || (next === 'multiplier' ? 1 : 100)) });
  };
  const setValue = (v) => {
    onChange({ rate_type: rateType, rate_value: v });
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3 text-xs">
        <label className="flex items-center gap-1 cursor-pointer" data-testid={`${idPrefix}-rate-none`}>
          <input type="radio" checked={rateType === 'none'} onChange={() => setType('none')} className="accent-slate-400" />
          <span style={{ color: '#94A3B8' }}>No rate</span>
        </label>
        <label className="flex items-center gap-1 cursor-pointer" data-testid={`${idPrefix}-rate-fixed`}>
          <input type="radio" checked={rateType === 'fixed'} onChange={() => setType('fixed')} className="accent-emerald-500" />
          <span style={{ color: '#94A3B8' }}>Fixed $/hr</span>
        </label>
        <label className="flex items-center gap-1 cursor-pointer" data-testid={`${idPrefix}-rate-multiplier`}>
          <input type="radio" checked={rateType === 'multiplier'} onChange={() => setType('multiplier')} className="accent-blue-500" />
          <span style={{ color: '#94A3B8' }}>Multiplier</span>
        </label>
      </div>
      {rateType !== 'none' && (
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: '#64748B' }}>
            {rateType === 'fixed' ? '$' : '×'}
          </span>
          <Input
            type="number"
            min="0"
            step={rateType === 'multiplier' ? '0.05' : '1'}
            value={rateValue ?? ''}
            onChange={(e) => setValue(e.target.value === '' ? null : parseFloat(e.target.value))}
            className="h-8 text-sm w-24"
            placeholder={rateType === 'fixed' ? '150' : '1.5'}
            data-testid={`${idPrefix}-rate-value`}
          />
          <span className="text-xs" style={{ color: '#64748B' }}>
            {rateType === 'fixed' ? 'per hour' : '× job base rate'}
          </span>
        </div>
      )}
    </div>
  );
}

export default function ManageLabelsDialog({ open, onOpenChange, onChanged }) {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // New-label form state
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(COLOR_PRESETS[8]);
  const [newRateType, setNewRateType] = useState('none');
  const [newRateValue, setNewRateValue] = useState(null);

  // Edit-row state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editRateType, setEditRateType] = useState('none');
  const [editRateValue, setEditRateValue] = useState(null);

  const fetchLabels = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/labels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabels(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load labels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) fetchLabels(); }, [open, fetchLabels]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) { toast.error('Label name is required'); return; }
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        name,
        color: newColor,
        rate_type: newRateType,
        rate_value: newRateType === 'none' ? null : (newRateValue || 0),
      };
      await axios.post(`${API}/api/labels`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewName('');
      setNewColor(COLOR_PRESETS[8]);
      setNewRateType('none');
      setNewRateValue(null);
      await fetchLabels();
      onChanged?.();
      toast.success(`Created label "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create label');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (label) => {
    setEditingId(label.id);
    setEditName(label.name);
    setEditColor(label.color);
    setEditRateType(label.rate_type || 'none');
    setEditRateValue(label.rate_value ?? null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditColor('');
    setEditRateType('none');
    setEditRateValue(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const name = editName.trim();
    if (!name) { toast.error('Label name is required'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/labels/${editingId}`, {
        name,
        color: editColor,
        rate_type: editRateType,
        rate_value: editRateType === 'none' ? null : (editRateValue || 0),
      }, { headers: { Authorization: `Bearer ${token}` } });
      cancelEdit();
      await fetchLabels();
      onChanged?.();
      toast.success('Label updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update label');
    }
  };

  const handleDelete = async (label) => {
    if (!window.confirm(`Delete the "${label.name}" label? It will be removed from every job that has it. This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/labels/${label.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchLabels();
      onChanged?.();
      toast.success(`Deleted label "${label.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete label');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="manage-labels-dialog">
        <DialogHeader>
          <DialogTitle>Manage Job Labels</DialogTitle>
          <DialogDescription>
            Color-coded tags with optional billing rates. Apply them on a job and
            invoices (Native PDF + QuickBooks) auto-bill at the resolved rate.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex items-start gap-2 rounded-lg px-3 py-2"
          style={{ background: 'rgba(30,136,229,0.08)', border: '1px solid rgba(30,136,229,0.25)' }}
        >
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#1E88E5' }} />
          <p className="text-xs" style={{ color: '#94A3B8' }}>
            When a job has multiple rate-bearing labels, the <strong className="text-white">single highest</strong> resolved rate wins —
            no stacking. Multipliers need the job to have a base service rate.
          </p>
        </div>

        {/* Create new label */}
        <div className="rounded-lg p-3 space-y-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid #2D3748' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
            Add a new label
          </p>
          <div className="flex items-center gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Overtime"
              maxLength={40}
              data-testid="new-label-name-input"
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            />
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className="rounded-full transition-all"
                  style={{
                    width: 18, height: 18, background: c,
                    border: newColor === c ? '2px solid #fff' : '2px solid transparent',
                    boxShadow: newColor === c ? `0 0 0 2px ${c}` : 'none',
                  }}
                  aria-label={`Pick color ${c}`}
                  data-testid={`new-label-color-${c.toLowerCase()}`}
                />
              ))}
            </div>
          </div>
          <RateConfig
            rateType={newRateType}
            rateValue={newRateValue}
            onChange={(v) => { setNewRateType(v.rate_type); setNewRateValue(v.rate_value); }}
            idPrefix="new-label"
          />
          <div className="flex items-center justify-between">
            {newName.trim() ? (
              <span className="flex items-center gap-2 text-xs" style={{ color: '#64748B' }}>
                Preview:&nbsp;
                <LabelChip label={{ name: newName.trim(), color: newColor }} />
                <RateSummary rateType={newRateType} rateValue={newRateValue} />
              </span>
            ) : <span />}
            <Button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              size="sm"
              data-testid="create-label-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create</>}
            </Button>
          </div>
        </div>

        {/* Existing labels */}
        <div className="max-h-[45vh] overflow-y-auto space-y-1.5">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : labels.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ color: '#64748B' }}>
              No labels yet. Add your first one above.
            </p>
          ) : (
            labels.map((label) => (
              <div
                key={label.id}
                className="rounded-lg px-3 py-2"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid #1F2937' }}
                data-testid={`label-row-${label.id}`}
              >
                {editingId === label.id ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        maxLength={40}
                        className="h-8 text-sm flex-1"
                        data-testid={`edit-label-name-${label.id}`}
                      />
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {COLOR_PRESETS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setEditColor(c)}
                            className="rounded-full transition-all"
                            style={{
                              width: 16, height: 16, background: c,
                              border: editColor === c ? '2px solid #fff' : '2px solid transparent',
                            }}
                            aria-label={`Pick color ${c}`}
                          />
                        ))}
                      </div>
                    </div>
                    <RateConfig
                      rateType={editRateType}
                      rateValue={editRateValue}
                      onChange={(v) => { setEditRateType(v.rate_type); setEditRateValue(v.rate_value); }}
                      idPrefix={`edit-label-${label.id}`}
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button onClick={cancelEdit} size="sm" variant="ghost" className="h-8">
                        Cancel
                      </Button>
                      <Button onClick={saveEdit} size="sm" data-testid={`save-label-${label.id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <LabelChip label={label} />
                      <RateSummary rateType={label.rate_type} rateValue={label.rate_value} />
                      {label.is_default && (
                        <span className="text-[10px] uppercase tracking-wider" style={{ color: '#64748B' }}>
                          default
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => startEdit(label)}
                      className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/5"
                      aria-label="Edit label"
                      data-testid={`edit-label-btn-${label.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(label)}
                      className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                      aria-label="Delete label"
                      data-testid={`delete-label-btn-${label.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
